<script setup lang="ts">
/**
 * Groep radioknoppen, optioneel met beschrijving per optie. Vervangt
 * `<v-input type="radio">`.
 *
 * `$attrs` gaan (anders dan bij de andere velden) naar de wrapper: er is geen enkel
 * control-element, en een `@change` vangt via bubbling elke knop.
 */
import { computed, ref } from 'vue'
import VFormField from './VFormField.vue'
import type { FormFieldBaseProps, SelectValue } from './types'
import { INVALID_CONTROL_CLASSES, useFieldErrors, useFormFieldBindings } from './useFormField'

const modelValue = defineModel<string | number | boolean | null>()

const props = withDefaults(
    defineProps<
        FormFieldBaseProps & {
            /**
             * VInput: `values`. Namen en beschrijvingen zijn vertaalsleutels. Ook een
             * object: zijn waarden tellen als opties (zoals VSelect).
             */
            options?: SelectValue[] | Record<string, SelectValue>
            radioBtnClasses?: string
            /**
             * - `list`: opties onder elkaar in één omkaderde lijst. Zo staan radio's bijna
             *   overal in de app (VInput: `input-classes="flex-col radio-list"`). Die
             *   klassen horen op het control, niet op het veld: anders krijgt het veldlabel
             *   mee de kader en staan de opties naast elkaar met dubbele randen.
             * - `inline`: opties naast elkaar, zonder kader.
             * - `bare`: geen opmaak, voor schermen die de opties zelf stylen (bv.
             *   License/Activate met kaarten). Dit is wat een kale VInput-radio rendert.
             * - `cards`: een kaart per optie, de gekozen krijgt een primary rand en lichte
             *   achtergrond; beschrijving eronder. Opvolger van VRadioOptionList (keuze klant
             *   25/09/2026: één radiocomponent).
             */
            variant?: 'list' | 'inline' | 'bare' | 'cards'
            /**
             * Enkel bij `cards`: compacte kaarten naast elkaar (wrap op smalle schermen), de
             * beschrijving als tooltip i.p.v. eronder.
             */
            grid?: boolean
        }
    >(),
    {
        options: () => [],
        radioBtnClasses: '',
        variant: 'list',
        grid: false,
        showErrors: true,
    }
)

const optionList = computed<SelectValue[]>(() =>
    Array.isArray(props.options) ? props.options : Object.values(props.options ?? {})
)

defineSlots<{
    /** Extra inhoud onder een optie, bv. een uitleg met link. */
    content?: (props: { value: SelectValue }) => unknown
    /** Eigen weergave van de optietekst (i.p.v. de vertaalde naam), bv. met een label. */
    option?: (props: { option: SelectValue }) => unknown
}>()

const errors = useFieldErrors(props)
const baseBindings = useFormFieldBindings(props, errors)

const VARIANT_CLASSES = {
    list: 'flex-col radio-list',
    inline: 'radio-inline flex-row flex-wrap items-start gap-x-6 gap-y-2',
    bare: '',
    cards: '',
} as const

/** Kaart per optie (variant cards), zelfde look als de vroegere VRadioOptionList. */
function cardClasses(option: SelectValue): (string | false)[] {
    // Los vergelijken, zoals de native radio's (v-model): bij model "1" en key 1 was de
    // radio aangevinkt maar de kaart niet gemarkeerd.
    const selected = modelValue.value != null && String(modelValue.value) === String(option.key)
    const disabled = props.disabled || option.disabled === true
    return [
        'flex gap-2 rounded-lg border transition-colors',
        props.grid ? 'min-w-[7rem] flex-1 items-center p-2.5' : 'items-start px-3 py-2',
        selected
            ? 'border-primary bg-primary/5 dark:bg-primary/10'
            : 'border-gray-300 hover:border-gray-400 dark:border-gray-600',
        disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
    ]
}

/** Klassen op de control-container: variant + eigen `inputClasses`. */
const containerClasses = computed<string>(() =>
    `${VARIANT_CLASSES[props.variant]} ${props.inputClasses ?? ''}`.trim()
)

const fieldBindings = computed(() => ({
    ...baseBindings.value,
    inputClasses: containerClasses.value,
}))

const fieldRef = ref<InstanceType<typeof VFormField> | null>(null)

/** Focust de gekozen optie, of anders de eerste die kiesbaar is (zoals de browser bij Tab). */
function focus(): void {
    const radios = Array.from(
        (fieldRef.value?.$el as HTMLElement | undefined)?.querySelectorAll<HTMLInputElement>(
            'input[type="radio"]'
        ) ?? []
    ).filter((radio) => !radio.disabled)

    ;(radios.find((radio) => radio.checked) ?? radios[0])?.focus()
}

defineExpose({ focus })

// VInput zette `inputClasses` ook op elke radio; voor `list` blijft dat zo (pariteit).
// Radio's kregen geen rondingen of breedte; `invalid` werd er stil genegeerd.
const classes = computed<string>(() => {
    const own = props.variant === 'list' ? containerClasses.value : (props.inputClasses ?? '')

    return `${own} ${props.invalid ? INVALID_CONTROL_CLASSES : ''}`
})
</script>

<template>
    <VFormField ref="fieldRef" v-bind="fieldBindings">
        <!-- v-field__raw: de kaartjes stylen zichzelf. De algemene .v-field-regels voor
             label/span (rechts uitgelijnd vanaf lg, py-2, basis-1/4) maakten ze groot en
             zetten tekst en radio rechts. -->
        <div
            v-if="variant === 'cards'"
            :class="[
                'v-field__raw',
                grid ? 'flex w-full flex-wrap gap-2' : 'flex w-full flex-col gap-2',
            ]"
        >
            <label
                v-for="option in optionList"
                :key="String(option.key)"
                :title="grid && option.description ? $t(option.description) : undefined"
                :class="cardClasses(option)"
            >
                <input
                    v-model="modelValue"
                    type="radio"
                    :name="name ?? undefined"
                    :value="option.key"
                    :disabled="disabled || option.disabled === true"
                    :class="[
                        'h-4 w-4 shrink-0 border-gray-300 text-primary focus:ring-primary',
                        !grid && 'mt-0.5',
                        invalid && INVALID_CONTROL_CLASSES,
                    ]"
                />
                <span class="flex flex-col">
                    <span class="text-sm font-medium text-gray-800 dark:text-gray-200">
                        <slot name="option" :option="option">{{ $t(option.name) }}</slot>
                    </span>
                    <span
                        v-if="option.description && !grid"
                        class="text-xs text-gray-500 dark:text-gray-400"
                    >
                        {{ $t(option.description) }}
                    </span>
                </span>
            </label>
        </div>
        <template v-else>
            <label v-for="option in optionList" :key="String(option.key)" :class="labelClasses">
                <div :class="['option', radioBtnClasses]">
                    <input
                        v-model="modelValue"
                        type="radio"
                        :name="name ?? undefined"
                        :value="option.key"
                        :disabled="disabled || option.disabled === true"
                        :class="classes"
                    />
                    <template v-if="!option.description">
                        <slot name="option" :option="option">{{ $t(option.name) }}</slot>
                    </template>
                    <template v-else>
                        <h4>{{ $t(option.name) }}</h4>
                    </template>
                </div>
                <template v-if="option.description">
                    <p>{{ $t(option.description) }}</p>
                </template>
                <slot name="content" v-if="$slots.content" :value="option"></slot>
            </label>
        </template>
    </VFormField>
</template>

<style scoped lang="scss">
// Opties naast elkaar. `.radio-inline` staat op de control-container (in VFormField,
// dus een andere scope: de layout daarvan zit in VARIANT_CLASSES). Enkel het laatste
// deel van elke selector is gescoped, dus dit raakt enkel onze eigen labels en radio's.
.radio-inline {
    label {
        @apply flex flex-col cursor-pointer py-2 text-sm font-medium;

        .option {
            @apply flex items-center gap-2;

            h4 {
                @apply m-0 inline-block;
            }
        }

        // Beschrijving onder de tekst, niet onder de radio (16px radio + 8px gap).
        p {
            @apply pl-6;
        }
    }

    // `.v-input input` geeft elk input flex-auto en een basis; een radio moet zijn
    // vaste breedte houden.
    input[type='radio'] {
        @apply flex-none m-0;
    }
}
</style>
